import { useState, useRef, useEffect } from 'react';

function ChatPanel({ chatHistory, onSendMessage, status, onCancel, isProcessing, liveStatus, isTyping, progressInfo }) {
  const [message, setMessage] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const totalTokens = chatHistory.reduce((sum, msg) => {
    const metadata = msg.metadata || {};
    return sum + (metadata.total_tokens || 0);
  }, 0);

  const totalInputTokens = chatHistory.reduce((sum, msg) => {
    const metadata = msg.metadata || {};
    return sum + (metadata.input_tokens || 0);
  }, 0);

  const totalOutputTokens = chatHistory.reduce((sum, msg) => {
    const metadata = msg.metadata || {};
    return sum + (metadata.output_tokens || 0);
  }, 0);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, isTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = () => {
    if (message.trim() && status.type !== 'loading') {
      onSendMessage(message);
      setMessage('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  };

  const formatMessage = (msg) => {
    if (typeof msg !== 'string') return msg;

    let cleaned = msg.replace(/```[\s\S]*?```/g, '');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    cleaned = cleaned.replace(/ {2,}/g, ' ');
    cleaned = cleaned.trim();

    const parts = cleaned.split(/(\*\*.*?\*\*)/g);

    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const quickPrompts = [
    "Create a sample notebook for data analysis",
    "Analyze the uploaded CSV files",
    "Add a cell to load and explore data",
    "Create visualizations for the dataset",
  ];

  return (
    <div className={`flex flex-col bg-white border-l border-gray-200 transition-all ${isExpanded ? 'w-96' : 'w-12'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        {isExpanded && (
          <>
            <div className="flex flex-col gap-1">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                AI Assistant
              </h3>
              {totalTokens > 0 && (
                <div className="text-xs text-gray-600 font-mono">
                  📊 Tokens: {totalTokens.toLocaleString()} (↑{totalInputTokens} ↓{totalOutputTokens})
                </div>
              )}
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
        {!isExpanded && (
          <button
            onClick={() => setIsExpanded(true)}
            className="text-gray-500 hover:text-gray-700 mx-auto"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
      </div>

      {isExpanded && (
        <>
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {chatHistory.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <p className="text-gray-500 mb-4">Start a conversation with the AI</p>
                <div className="space-y-2">
                  <p className="text-xs text-gray-400 font-semibold">Quick starts:</p>
                  {quickPrompts.map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => setMessage(prompt)}
                      className="block w-full text-left text-xs p-2 bg-blue-50 hover:bg-blue-100 rounded text-blue-700 transition-colors"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {chatHistory.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-4 py-2 ${msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-800'
                        }`}
                    >
                      <div className="text-sm whitespace-pre-wrap">{formatMessage(msg.content)}</div>
                      {msg.metadata?.actions && msg.metadata.actions.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-300 text-xs opacity-75">
                          {msg.metadata.actions.map((action, actionIdx) => (
                            <div key={actionIdx} className="flex items-center gap-1">
                              <span>• {action.action}</span>
                              {action.run_this_cell && <span className="text-green-600">(executed)</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {msg.metadata?.total_tokens > 0 && msg.role === 'assistant' && (
                        <div className="mt-2 pt-2 border-t border-gray-300 text-xs opacity-60 font-mono">
                          📤 {msg.metadata.total_tokens} tokens (↑{msg.metadata.input_tokens} ↓{msg.metadata.output_tokens})
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                        <span className="text-xs text-gray-600">AI is typing...</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Loading/Progress indicator */}
                {status.type === 'loading' && !isTyping && (
                  <div className="flex justify-start w-full">
                    <div className="bg-gray-100 rounded-lg px-4 py-2 w-full max-w-[85%]">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1">
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></span>
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></span>
                              <span className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></span>
                            </div>
                            <span className="text-xs text-gray-500">{liveStatus || status.message}</span>
                          </div>

                          {isProcessing && onCancel && (
                            <button
                              onClick={onCancel}
                              className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded hover:bg-red-200 transition-colors flex items-center gap-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Stop
                            </button>
                          )}
                        </div>

                        {/* Progress bar */}
                        {progressInfo && (
                          <div className="w-full">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-500">{progressInfo.actionType}</span>
                              <span className="text-xs text-gray-500 font-mono">
                                {progressInfo.current}/{progressInfo.total}
                              </span>
                            </div>
                            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 transition-all duration-300 ease-out"
                                style={{ width: `${(progressInfo.current / progressInfo.total) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-1">
            <div className="flex gap-2">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  adjustTextareaHeight();
                }}
                onKeyDown={handleKeyDown}
                placeholder="Ask AI to help with your notebook..."
                className="flex-1 px-1 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows="4"
                disabled={status.type === 'loading'}

              />
              {/* <button
                onClick={handleSend}
                disabled={!message.trim() || status.type === 'loading'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button> */}
            </div>
            <div className="border-t border-gray-200 p-1 absolute right-2">
              <div className="flex gap-2">

                <button
                  onClick={handleSend}
                  disabled={!message.trim() || status.type === 'loading'}
                  className="px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  send
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Press Enter to send, Shift + Enter for new line
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default ChatPanel;